import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from './Header';
import SEO from './SEO';
import { getIngredientBySlug } from '../data/ingredientData';
import { trackAppStoreCtaClick } from '../utils/analytics';
import './IngredientDetail.css';

function IngredientDetail() {
  const { slug } = useParams();
  const ingredient = getIngredientBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!ingredient) {
    return (
      <div className="App">
        <SEO
          title="Ingredient Not Found | Acne Safe"
          description="This ingredient could not be found in the Acne Safe database."
        />
        <Header />
        <main className="ingredient-detail-page">
          <Link to="/ingredients" className="back-to-ingredients">
            All Ingredients
          </Link>
          <section className="ingredient-detail-message">
            Ingredient not found.
          </section>
        </main>
      </div>
    );
  }

  const canonicalUrl = `https://www.useacnesafe.com/ingredients/${ingredient.slug}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${ingredient.name} — Is It Acne Safe?`,
    description: ingredient.description,
    author: { '@type': 'Organization', name: 'Acne Safe' },
    publisher: {
      '@type': 'Organization',
      name: 'Acne Safe',
      url: 'https://www.useacnesafe.com',
    },
    mainEntityOfPage: canonicalUrl,
  };

  return (
    <div className="App">
      <SEO
        title={`${ingredient.name} — Is It Acne Safe? | Acne Safe`}
        description={`${ingredient.name} appears in the Acne Safe screening database. Review its uses, aliases, evidence limitations, and products that may contain it.`}
        keywords={`${ingredient.name}, pore clogging ingredient, comedogenic, acne safe, ${ingredient.category}, ${(ingredient.alsoKnownAs || []).join(', ')}`}
        url={canonicalUrl}
        type="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Header />

      <main className="ingredient-detail-page">
        <Link to="/ingredients" className="back-to-ingredients">
          All Ingredients
        </Link>

        <article className="ingredient-detail-content">
          <header className="ingredient-detail-header">
            <span className="ingredient-category-badge">
              {ingredient.category}
            </span>
            <h1>{ingredient.name}</h1>
            {ingredient.alsoKnownAs && ingredient.alsoKnownAs.length > 0 && (
              <p className="ingredient-aliases">
                Also known as: {ingredient.alsoKnownAs.join(', ')}
              </p>
            )}
          </header>

          <section className="ingredient-detail-section">
            <h2>What is {ingredient.name}?</h2>
            <p>{ingredient.description}</p>
          </section>

          <section className="ingredient-detail-section">
            <h2>Why is it used in products?</h2>
            <p>{ingredient.whyUsed}</p>
          </section>

          <section className="ingredient-detail-section">
            <h2>Why is it commonly flagged?</h2>
            <p>{ingredient.whyClogsPores}</p>
            <p>
              This ingredient-level screening signal does not prove that a finished product will cause acne.{' '}
            </p>
          </section>

          {ingredient.commonProducts && ingredient.commonProducts.length > 0 && (
            <section className="ingredient-detail-section">
              <h2>Commonly found in</h2>
              <div className="ingredient-product-chips">
                {ingredient.commonProducts.map((product) => (
                  <span key={product} className="ingredient-product-chip">
                    {product}
                  </span>
                ))}
              </div>
            </section>
          )}

          <section className="ingredient-detail-cta">
            <h3>Want to check a product's full ingredient list?</h3>
            <p>
              Scan any label with the Acne Safe app or{' '}
              <Link to="/acne-safe-ingredient-checker">
                check the complete list with our Acne Safe Ingredient Checker
              </Link>.
            </p>
            <a
              href="https://apps.apple.com/us/app/acne-safe-ingredient-scanner/id6747936538"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button-ingredient"
              onClick={() => trackAppStoreCtaClick('ingredient_detail')}
            >
              <img
                src="/download.png"
                alt="Download on App Store"
                className="download-button-image"
              />
            </a>
          </section>
        </article>
      </main>
    </div>
  );
}

export default IngredientDetail;
